import { type EditorRoom, type EditorSurface, type Subfloor } from "./document"
import { centroid } from "./geometry"

// The material schedule's two derivations: which subfloor a room resolves to,
// and what order the rows print in. Pure and dependency-free (vitest-covered)
// so the printed document and the editor inspector compute them from the same
// code — a second implementation on the Rails side would be a drift surface
// for a value an installer acts on.

/**
 * A cropped plan image as the editor places it on its sheet. Bounds are
 * [x, y] plus width/height at the image's own scale, matching how Editor.tsx
 * both sizes the sheet extent and positions each image group.
 */
export type PlacedImage = {
  id: number | string
  x: number
  y: number
  scale: number
  width: number | null
  height: number | null
  subfloor: Subfloor | null
}

/**
 * Room override -> sheet-image default -> project default.
 *
 * The middle tier is the cropped IMAGE, not the sheet and not the plan set:
 * first and second floor are normally cropped from the same PDF onto one
 * composite sheet, so only the crop discriminates between them. The crop is
 * the floor level.
 *
 * A room resolves its tier by hit-testing its centroid against the sheet's
 * placed images. That is stable because SheetImage#transform_locked_when_
 * rooms_exist freezes placement, scale, crop, and rotation the moment any
 * room is traced — the images cannot move out from under the rooms.
 *
 * Sheets with no images (a whole-page `pdf_page` sheet, or a blank sheet)
 * fall straight through to the project default, as does a room whose
 * centroid lands in a gap between crops.
 */
export function resolveSubfloor(
  room: Pick<EditorRoom, "outline" | "subfloor">,
  images: PlacedImage[],
  projectDefault: Subfloor
): Subfloor {
  if (room.subfloor) return room.subfloor
  const image = imageUnderRoom(room.outline, images)
  return image?.subfloor ?? projectDefault
}

/** The placed image a room's centroid falls inside, or null. */
export function imageUnderRoom(
  outline: EditorRoom["outline"],
  images: PlacedImage[]
): PlacedImage | null {
  if (outline.length < 3) return null
  const [cx, cy] = centroid(outline)
  for (const image of images) {
    if (image.width == null || image.height == null) continue
    const right = image.x + image.width * image.scale
    const bottom = image.y + image.height * image.scale
    if (cx >= image.x && cx <= right && cy >= image.y && cy <= bottom) return image
  }
  return null
}

// Generic in the room type so callers that have already measured their rooms
// (the editor's ComputedRoom) keep those quantities through the grouping.
export type ScheduleRow<R extends EditorRoom = EditorRoom> =
  | { kind: "room"; id: string; room: R }
  | { kind: "surface"; id: string; surface: EditorSurface; attached: boolean }

/**
 * Print order: each room, then the surfaces attached to it, then every
 * unattached surface. Rooms and surfaces each arrive in their own array
 * order, which IS their persisted position.
 *
 * Attachment is the whole ordering mechanism — a drag in the editor rewrites
 * a surface's parent and position, so there is no second source of truth to
 * fall out of sync when a room is added, renamed, or reordered.
 */
export function scheduleRows<R extends EditorRoom>(
  rooms: R[],
  surfaces: EditorSurface[]
): ScheduleRow<R>[] {
  const roomIds = new Set(rooms.map((room) => room.id))
  const byParent = new Map<string, EditorSurface[]>()
  const orphans: EditorSurface[] = []

  for (const surface of surfaces) {
    // A parent that no longer exists (its room was deleted) degrades to
    // unattached rather than vanishing from the document.
    if (surface.parentRoomId && roomIds.has(surface.parentRoomId)) {
      const list = byParent.get(surface.parentRoomId)
      if (list) list.push(surface)
      else byParent.set(surface.parentRoomId, [surface])
    } else {
      orphans.push(surface)
    }
  }

  const rows: ScheduleRow<R>[] = []
  for (const room of rooms) {
    rows.push({ kind: "room", id: room.id, room })
    for (const surface of byParent.get(room.id) ?? []) {
      rows.push({ kind: "surface", id: surface.id, surface, attached: true })
    }
  }
  for (const surface of orphans) {
    rows.push({ kind: "surface", id: surface.id, surface, attached: false })
  }
  return rows
}

/**
 * The keyboard equivalent of dragging a row one step, as arguments for
 * `moveRowAfter`: "print after this id". `undefined` means the direction is
 * unavailable (the button is disabled); `null` means the very first slot.
 *
 * Moving up targets the row TWO above, because the action places a row
 * *after* its target — for a surface that walks it into the group above, and
 * for a room it steps past the room ahead of it.
 */
export function moveTargets(
  rows: ScheduleRow<EditorRoom>[],
  index: number
): { up: string | null | undefined; down: string | undefined } {
  const row = rows[index]
  if (!row) return { up: undefined, down: undefined }

  let up: string | null | undefined
  if (index === 0) up = undefined
  // Only a room can take the very first slot; a surface has nothing above
  // its own group to attach to.
  else if (index === 1) up = row.kind === "room" ? null : undefined
  else up = rows[index - 2].id

  let down: string | undefined
  if (row.kind === "room") {
    // Step past the next room AND everything attached to it, or the move
    // would just swap the room with its neighbour's first surface.
    const nextRoom = rows.findIndex((r, i) => i > index && r.kind === "room")
    if (nextRoom === -1) {
      down = undefined
    } else {
      let last = nextRoom
      while (last + 1 < rows.length && rows[last + 1].kind === "surface") last++
      down = rows[last].id
    }
  } else {
    down = index + 1 < rows.length ? rows[index + 1].id : undefined
  }

  return { up, down }
}
